import React, { useState, useEffect } from 'react';
import { authAPI } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Profile = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: ''
    });
    const [errors, setErrors] = useState({});
    const [hoveredReset, setHoveredReset] = useState(false);
    const [hoveredUpdate, setHoveredUpdate] = useState(false);

    useEffect(() => {
        fetchUserProfile();
    }, []);

    const fetchUserProfile = async () => {
        setLoading(true);
        try {
            const response = await authAPI.me();
            if (response.data.success) {
                const userData = response.data.data;
                setUser(userData);
                setFormData({
                    name: userData.name || '',
                    email: userData.email || '',
                    phone: userData.phone || '',
                    address: userData.address || ''
                });
            } else {
                toast.error(response.data.message || 'Failed to load profile');
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            if (error.response?.status === 401) {
                toast.error('Session expired. Please login again.');
                setTimeout(() => {
                    window.location.href = '/login';
                }, 2000);
            } else {
                toast.error(error.response?.data?.message || 'Failed to load profile');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData({ ...formData, [id]: value });
        if (errors[id]) {
            setErrors({ ...errors, [id]: '' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUpdating(true);
        setErrors({});

        try {
            const response = await authAPI.updateProfile(formData);
            if (response.data.success) {
                toast.success('Profile updated successfully!');
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                const updatedUser = { ...storedUser, ...formData };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                await fetchUserProfile();
            } else {
                toast.error(response.data.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Update error:', error);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
                toast.error('Please fix the validation errors');
            } else if (error.response?.status === 401) {
                toast.error('Session expired. Please login again.');
                setTimeout(() => {
                    window.location.href = '/login';
                }, 2000);
            } else {
                toast.error(error.response?.data?.message || 'Failed to update profile');
            }
        } finally {
            setUpdating(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: user?.name || '',
            email: user?.email || '',
            phone: user?.phone || '',
            address: user?.address || ''
        });
        setErrors({});
    };

    // ----- inline styles -----
    const mainContentStyle = {
        marginLeft: '260px',
        padding: '20px 30px',
        backgroundColor: '#f8f9fa',
        minHeight: '100vh',
    };

    const containerStyle = {
        maxWidth: '1200px',
        margin: '0 auto',
    };

    const pageHeaderStyle = {
        marginBottom: '1.5rem',
    };

    const pageTitleStyle = {
        fontSize: '1.3rem',
        fontWeight: 700,
        color: '#0b2b3b',
        margin: '0 0 0.25rem',
    };

    const pageSubtitleStyle = {
        fontSize: '0.8rem',
        color: '#64748b',
        margin: 0,
    };

    const rowStyle = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1.5rem',
    };

    const sidebarColStyle = {
        flex: '0 0 33.333%',
        maxWidth: '33.333%',
    };

    const formColStyle = {
        flex: '0 0 66.666%',
        maxWidth: '66.666%',
    };

    const cardStyle = {
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
    };

    const cardHoverStyle = {
        boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
    };

    const cardBodyStyle = {
        padding: 0,
    };

    const coverStyle = {
        height: '100px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    };

    const avatarWrapperStyle = {
        textAlign: 'center',
        marginTop: '-50px',
        marginBottom: '20px',
    };

    const avatarContainerStyle = {
        position: 'relative',
        display: 'inline-block',
    };

    const avatarCircleStyle = {
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        background: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        border: '4px solid white',
    };

    const avatarInnerStyle = {
        width: '92px',
        height: '92px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    const avatarIconStyle = {
        fontSize: '3rem',
        color: 'white',
    };

    const cameraButtonStyle = {
        position: 'absolute',
        bottom: 0,
        right: 0,
        padding: '0.25rem 0.5rem',
        background: 'white',
        border: 'none',
        borderRadius: '50%',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        cursor: 'pointer',
        fontSize: '0.7rem',
        transform: 'translate(0, 0)',
    };

    const userInfoStyle = {
        textAlign: 'center',
        padding: '0 1.5rem 1rem',
    };

    const userNameStyle = {
        fontSize: '1.25rem',
        fontWeight: 'bold',
        marginBottom: '0.25rem',
    };

    const badgeStyle = {
        display: 'inline-block',
        padding: '0.25rem 0.75rem',
        background: '#0b2b3b',
        color: 'white',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: 500,
        marginBottom: '0.75rem',
    };

    const staffIdStyle = {
        fontSize: '0.8rem',
        color: '#64748b',
        marginBottom: 0,
    };

    const hrStyle = {
        margin: '0.5rem 0',
        border: 'none',
        borderTop: '1px solid #e2e8f0',
    };

    const contactInfoStyle = {
        padding: '1rem 1.5rem',
    };

    const contactTitleStyle = {
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        color: '#64748b',
        marginBottom: '0.75rem',
    };

    const contactItemStyle = {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '0.5rem',
    };

    const contactIconWrapperStyle = {
        width: '35px',
        height: '35px',
        borderRadius: '50%',
        background: '#e8f4f8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '0.75rem',
    };

    const contactLabelStyle = {
        fontSize: '0.7rem',
        color: '#64748b',
        marginBottom: 0,
    };

    const contactValueStyle = {
        fontWeight: 600,
        marginBottom: 0,
    };

    // Form card styles
    const formCardStyle = {
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        overflow: 'hidden',
    };

    const formCardHeaderStyle = {
        padding: '1rem 1.5rem 0',
        borderBottom: 'none',
        background: 'transparent',
    };

    const formCardTitleStyle = {
        fontSize: '1rem',
        fontWeight: 600,
        color: '#0b2b3b',
        marginBottom: 0,
    };

    const formCardSubStyle = {
        fontSize: '0.8rem',
        color: '#64748b',
        marginTop: '0.25rem',
    };

    const formCardBodyStyle = {
        padding: '1.5rem',
    };

    const formRowStyle = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '1rem',
    };

    const formGroupStyle = (flex = 1) => ({
        flex: flex,
        minWidth: '200px',
    });

    const labelStyle = {
        display: 'block',
        marginBottom: '0.25rem',
        fontWeight: 600,
        fontSize: '0.9rem',
        color: '#1e293b',
    };

    const requiredAsteriskStyle = {
        content: '" *"',
        color: 'red',
    };

    const inputStyle = (hasError, isDisabled = false) => ({
        width: '100%',
        padding: '0.5rem 0.75rem',
        fontSize: '0.9rem',
        borderRadius: '10px',
        border: `1px solid ${hasError ? '#ef4444' : '#e2e8f0'}`,
        background: isDisabled ? '#f8f9fa' : 'white',
        outline: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        ...(isDisabled && { cursor: 'not-allowed' }),
    });

    const textareaStyle = (hasError) => ({
        ...inputStyle(hasError),
        resize: 'vertical',
        minHeight: '80px',
    });

    const errorStyle = {
        color: '#ef4444',
        fontSize: '0.8rem',
        marginTop: '0.25rem',
    };

    const smallTextStyle = {
        fontSize: '0.7rem',
        color: '#64748b',
        display: 'block',
        marginTop: '0.25rem',
    };

    const actionRowStyle = {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '1rem',
        marginTop: '1rem',
    };

    const resetButtonStyle = (hovered) => ({
        padding: '0.5rem 1.5rem',
        background: hovered ? '#e2e8f0' : '#f1f5f9',
        border: 'none',
        borderRadius: '8px',
        fontSize: '0.85rem',
        fontWeight: 600,
        color: '#475569',
        cursor: 'pointer',
        transition: 'background 0.2s',
    });

    const updateButtonStyle = (hovered, disabled) => ({
        padding: '0.5rem 1.5rem',
        background: disabled ? '#94a3b8' : (hovered ? '#5a67d8' : '#667eea'),
        border: 'none',
        borderRadius: '8px',
        fontSize: '0.85rem',
        fontWeight: 600,
        color: 'white',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.2s, transform 0.1s',
        transform: hovered && !disabled ? 'translateY(-1px)' : 'none',
        boxShadow: hovered && !disabled ? '0 4px 12px rgba(102,126,234,0.4)' : 'none',
    });

    // Loading spinner
    const spinnerContainerStyle = {
        marginLeft: '260px',
        padding: '20px 30px',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
    };

    if (loading) {
        return (
            <div style={spinnerContainerStyle}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '3rem', color: '#2c7da0' }}></i>
                    <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading profile...</p>
                </div>
                <ToastContainer />
            </div>
        );
    }

    return (
        <div style={mainContentStyle}>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />

            <div style={containerStyle}>
                {/* Page Header */}
                <div style={pageHeaderStyle}>
                    <h1 style={pageTitleStyle}>
                        <i className="fas fa-user" style={{ marginRight: '0.5rem' }}></i> My Profile
                    </h1>
                    <p style={pageSubtitleStyle}>View and update your profile information</p>
                </div>

                <div style={rowStyle}>
                    {/* Sidebar */}
                    <div style={sidebarColStyle}>
                        <div style={cardStyle}>
                            <div style={cardBodyStyle}>
                                <div style={coverStyle}></div>

                                <div style={avatarWrapperStyle}>
                                    <div style={avatarContainerStyle}>
                                        <div style={avatarCircleStyle}>
                                            <div style={avatarInnerStyle}>
                                                <i className="fas fa-user" style={avatarIconStyle}></i>
                                            </div>
                                        </div>
                                        <button
                                            style={cameraButtonStyle}
                                            onClick={() => document.getElementById('avatarUpload')?.click()}
                                        >
                                            <i className="fas fa-camera"></i>
                                        </button>
                                        <input type="file" id="avatarUpload" style={{ display: 'none' }} accept="image/*" />
                                    </div>
                                </div>

                                <div style={userInfoStyle}>
                                    <h3 style={userNameStyle}>{user?.name}</h3>
                                    <span style={badgeStyle}>{user?.role_display || user?.role}</span>
                                    <p style={staffIdStyle}>
                                        <i className="fas fa-id-card" style={{ marginRight: '0.25rem' }}></i> Staff ID: {user?.staff_id}
                                    </p>
                                </div>

                                <hr style={hrStyle} />

                                <div style={contactInfoStyle}>
                                    <h6 style={contactTitleStyle}>
                                        <i className="fas fa-address-card" style={{ marginRight: '0.5rem' }}></i> Contact Information
                                    </h6>

                                    <div style={contactItemStyle}>
                                        <div style={contactIconWrapperStyle}>
                                            <i className="fas fa-envelope text-primary"></i>
                                        </div>
                                        <div>
                                            <label style={contactLabelStyle}>Email Address</label>
                                            <p style={contactValueStyle}>{user?.email}</p>
                                        </div>
                                    </div>

                                    <div style={contactItemStyle}>
                                        <div style={contactIconWrapperStyle}>
                                            <i className="fas fa-phone text-success"></i>
                                        </div>
                                        <div>
                                            <label style={contactLabelStyle}>Phone Number</label>
                                            <p style={contactValueStyle}>{user?.phone || 'Not provided'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <div style={formColStyle}>
                        <div style={formCardStyle}>
                            <div style={formCardHeaderStyle}>
                                <h5 style={formCardTitleStyle}>
                                    <i className="fas fa-edit" style={{ marginRight: '0.5rem', color: '#667eea' }}></i>
                                    Edit Profile Information
                                </h5>
                                <p style={formCardSubStyle}>Update your personal information</p>
                            </div>
                            <div style={formCardBodyStyle}>
                                <form onSubmit={handleSubmit}>
                                    <div style={formRowStyle}>
                                        <div style={formGroupStyle(1)}>
                                            <label style={labelStyle}>
                                                Full Name <span style={requiredAsteriskStyle}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                style={inputStyle(!!errors.name)}
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter your full name"
                                            />
                                            {errors.name && <div style={errorStyle}>{errors.name[0]}</div>}
                                        </div>
                                    </div>

                                    <div style={formRowStyle}>
                                        <div style={formGroupStyle(1)}>
                                            <label style={labelStyle}>Email Address</label>
                                            <input
                                                type="email"
                                                id="email"
                                                style={inputStyle(false, true)}
                                                value={formData.email}
                                                disabled
                                            />
                                            <small style={smallTextStyle}>
                                                <i className="fas fa-info-circle" style={{ marginRight: '0.25rem' }}></i>
                                                Email cannot be changed
                                            </small>
                                            {errors.email && <div style={errorStyle}>{errors.email[0]}</div>}
                                        </div>

                                        <div style={formGroupStyle(1)}>
                                            <label style={labelStyle}>Phone Number</label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                style={inputStyle(!!errors.phone)}
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="Enter your phone number"
                                            />
                                            {errors.phone && <div style={errorStyle}>{errors.phone[0]}</div>}
                                        </div>
                                    </div>

                                    <div style={formRowStyle}>
                                        <div style={formGroupStyle(1)}>
                                            <label style={labelStyle}>Address</label>
                                            <textarea
                                                id="address"
                                                style={textareaStyle(!!errors.address)}
                                                rows="4"
                                                value={formData.address}
                                                onChange={handleChange}
                                                placeholder="Enter your complete address"
                                            />
                                            {errors.address && <div style={errorStyle}>{errors.address[0]}</div>}
                                        </div>
                                    </div>

                                    <hr style={hrStyle} />

                                    <div style={actionRowStyle}>
                                        <button
                                            type="button"
                                            style={resetButtonStyle(hoveredReset)}
                                            onMouseEnter={() => setHoveredReset(true)}
                                            onMouseLeave={() => setHoveredReset(false)}
                                            onClick={resetForm}
                                        >
                                            <i className="fas fa-undo-alt" style={{ marginRight: '0.5rem' }}></i>
                                            Reset
                                        </button>
                                        <button
                                            type="submit"
                                            style={updateButtonStyle(hoveredUpdate, updating)}
                                            onMouseEnter={() => setHoveredUpdate(true)}
                                            onMouseLeave={() => setHoveredUpdate(false)}
                                            disabled={updating}
                                        >
                                            <i className={`fas ${updating ? 'fa-spinner fa-spin' : 'fa-save'}`} style={{ marginRight: '0.5rem' }}></i>
                                            {updating ? 'Saving...' : 'Update Profile'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;